/**
 * The generation prompt writer on the Ideas board: the "generation-prompt-writer" skill
 * (skills/generation-prompt-writer, a copy of D:\Projects\Skills) as a format of the Think up
 * card. Set to Prompt, the card asks a text model to write a ready-to-paste prompt for a Video
 * or an Image node by the skill's method: the skill's files go along as the model's
 * instructions, with the generator's own facts (the lengths it allows, its prompt limit, how it
 * names references, whether it makes sound) and what the workspace holds (its Reference nodes,
 * its characters, props and places). The answer is a prompt file in the skill's format, which
 * lands beside the card as a card of its own; the paste in it goes onto a node on the canvas,
 * and when a generation comes out wrong the card asks for a fix at the source.
 *
 * Pure functions over text and the model catalog; the skill's files are read on demand.
 */
import { AI_MODELS, getImageModels, getVideoModels, type ModelDefinition, type ModelParameterOption } from '../models/config';
import { PROVIDER_NAMES, shortModelLabel } from '../components/nodes/composer/modelLabels';
import { NodeType, type CanvasNode } from '../types';
import type { TextRequest } from './textModel';

/** Which node the prompt is for */
export type PromptTarget = 'video' | 'image';

export const PROMPT_TARGETS: { id: PromptTarget; label: string; node: string }[] = [
    { id: 'video', label: 'Video', node: 'Video node' },
    { id: 'image', label: 'Image', node: 'Image node' },
];

/** What a prompt card was written for: the node, the generator, and the settings the prompt assumes */
export interface PromptFor {
    node: PromptTarget;
    /** The generator's id in the catalog (models/config.ts); '' when none was chosen */
    model: string;
    /** As the generator writes it ("12s", or "8") */
    duration?: string;
    aspectRatio?: string;
}

/** A prompt card taken to the canvas: the node to make, with the paste as its prompt */
export interface PromptToCanvas extends PromptFor {
    title: string;
    prompt: string;
}

// ── The generators ───────────────────────────────────────────────────────────

export interface GeneratorOption { id: string; label: string; group: string }

/** The generators a prompt can be written for: the app's video or image models, as the nodes name them */
export const promptGenerators = (target: PromptTarget): GeneratorOption[] =>
    (target === 'video' ? getVideoModels() : getImageModels())
        .filter(model => model.id !== 'comfyui-custom-workflow')
        .map(model => ({ id: model.id, label: shortModelLabel(model.label), group: PROVIDER_NAMES[model.provider] ?? model.provider }));

/** The generator a prompt is written for when none is chosen: the ones the skill's method was written on */
export const DEFAULT_GENERATOR: Record<PromptTarget, string> = { video: 'venice-seedance-2-5', image: 'venice-nano-banana-pro' };

/** The generator by id, of the right kind; the default of the kind when the id names none */
export const generatorOf = (target: PromptTarget, id: string | undefined | null): ModelDefinition | null => {
    const model = id ? AI_MODELS[id] : undefined;
    if (model && model.type === target) return model;
    return AI_MODELS[DEFAULT_GENERATOR[target]] ?? null;
};

export const lengthsOf = (model: ModelDefinition | null): ModelParameterOption[] => model?.supportedDurations ?? [];
export const framesOf = (model: ModelDefinition | null): ModelParameterOption[] => model?.supportedRatios ?? [];

const secondsOf = (value: string): number => Number(/\d+(\.\d+)?/.exec(value)?.[0] ?? NaN);

/** The length chosen, when the generator allows it; else the one nearest twelve seconds, room for a few shots */
export const lengthOf = (model: ModelDefinition | null, chosen?: string | null): string | undefined => {
    const options = lengthsOf(model);
    if (options.length === 0) return undefined;
    if (chosen && options.some(option => option.value === chosen)) return chosen;
    return [...options].sort((a, b) => Math.abs(secondsOf(a.value) - 12) - Math.abs(secondsOf(b.value) - 12) || secondsOf(b.value) - secondsOf(a.value))[0].value;
};

/** The frame chosen, when the generator allows it; else widescreen, else the generator's first */
export const frameOf = (model: ModelDefinition | null, chosen?: string | null): string | undefined => {
    const options = framesOf(model);
    if (options.length === 0) return undefined;
    if (chosen && options.some(option => option.value === chosen)) return chosen;
    return options.find(option => option.value === '16:9')?.value ?? options[0].value;
};

/** "4 to 30 seconds" when the lengths run without a gap, else "4, 8 or 12 seconds" */
const lengthsSaid = (options: ModelParameterOption[]): string => {
    const seconds = options.map(option => secondsOf(option.value)).filter(Number.isFinite).sort((a, b) => a - b);
    if (seconds.length === 0) return '';
    if (seconds.length > 2 && seconds.every((s, i) => i === 0 || s - seconds[i - 1] === 1)) return `${seconds[0]} to ${seconds[seconds.length - 1]} seconds`;
    return seconds.length === 1 ? `${seconds[0]} seconds` : `${seconds.slice(0, -1).join(', ')} or ${seconds[seconds.length - 1]} seconds`;
};

/**
 * What the model is told about the generator: its name, the lengths it allows and the one
 * chosen, the frame, its prompt limit, how it takes references and names them, and its sound.
 */
export const generatorNotes = (model: ModelDefinition | null, target: PromptTarget, chosen: { duration?: string; aspectRatio?: string } = {}): string => {
    if (!model) return target === 'video' ? 'The generator is not known: write for a generator like Seedance, with native sound.' : 'The generator is not known: write for a generator like Nano Banana Pro.';
    const lines: string[] = [`THE GENERATOR is ${shortModelLabel(model.label)}.`];
    if (target === 'video') {
        const lengths = lengthsOf(model);
        const duration = lengthOf(model, chosen.duration);
        if (lengths.length > 0) lines.push(`It makes clips of ${lengthsSaid(lengths)}.${duration ? ` This one is exactly ${secondsOf(duration)} seconds long: time every shot within that, and end on the held pose.` : ''}`);
        const frame = frameOf(model, chosen.aspectRatio);
        if (frame) lines.push(`The frame is ${frame} (set in the node, not in the paste).`);
        const modes = model.supportedVideoModes ?? ['text-to-video'];
        if (modes.includes('reference-to-video')) {
            const extras = [model.videoInputModes?.includes('reference-to-video') ? 'clips as @Video1…' : null, model.audioInputModes?.includes('reference-to-video') ? 'audio as @Audio1…' : null].filter(Boolean);
            lines.push(model.referenceElements
                ? 'In its reference mode it takes pictures of characters and objects, named @Element1, @Element2… in the prompt, and pictures of the scene, named @Image1, @Image2…'
                : `In its reference mode it takes up to ${model.maxReferenceImages ?? 9} pictures, named @Image1, @Image2… in the prompt in the order attached${extras.length ? `, and ${extras.join(' and ')}` : ''}.`);
        } else if (modes.includes('frames-to-video')) {
            lines.push('It takes a first frame, or a first and a last frame, as pictures; otherwise words alone. A picture given as the first frame IS the first frame, so it must show the scene as the film opens.');
        } else if (modes.includes('image-to-video')) {
            lines.push('It takes one picture as the first frame, or words alone (text to video). A picture given as the first frame IS the first frame, so it must show the scene as the film opens: an empty plate, never the character already in place.');
        } else {
            lines.push('It takes no pictures: describe everything in words.');
        }
        if (model.supportsAudioToggle) lines.push('It makes sound with the picture; the sound can be switched off in the node. Write the SOUND paragraph as the method says.');
        else if (model.audioAlwaysOn) lines.push('It makes sound with the picture, and the sound cannot be switched off. Write the SOUND paragraph as the method says.');
    } else {
        if (model.supportsImageReference) lines.push(`It takes up to ${model.maxReferenceImages ?? 1} reference picture${(model.maxReferenceImages ?? 1) === 1 ? '' : 's'}, named @Image1, @Image2… in the prompt in the order attached.`);
        else lines.push('It takes no reference pictures: describe everything in words.');
        const frame = frameOf(model, chosen.aspectRatio);
        if (frame) lines.push(`The frame is ${frame} (set in the node, not in the paste).`);
    }
    if (model.promptCharacterLimit) lines.push(`Its prompt holds at most ${model.promptCharacterLimit.toLocaleString('en-US')} characters: the paste must fit, with room to spare.`);
    return lines.join('\n');
};

// ── What the workspace holds ─────────────────────────────────────────────────

const shortText = (text: string | undefined, max: number): string => (text ?? '').replace(/\s+/g, ' ').trim().slice(0, max);

/**
 * The workspace's references and entities, for the prompt to name: its Reference nodes (a
 * picture, a clip or an audio file each, with the tag someone gave it), and its Character, Prop
 * and Location nodes with their descriptions. Empty when the workspace holds none.
 */
export const workspaceNotes = (nodes: ReadonlyArray<CanvasNode>): string => {
    const pictures: string[] = [];
    const clips: string[] = [];
    const audio: string[] = [];
    const characters: string[] = [];
    const props: string[] = [];
    const places: string[] = [];
    const quoted = (title: string | undefined, fallback: string) => `"${shortText(title, 60) || fallback}"`;
    for (const node of nodes) {
        const data = node.data ?? {};
        if (node.type === NodeType.MEDIA_REFERENCE || node.type === NodeType.AUDIO_REFERENCE) {
            const tag = data.referenceTag ? ` (${data.referenceTag})` : '';
            if (data.audioUrl || data.referenceKind === 'audio') audio.push(quoted(data.title, 'Audio'));
            else if (data.videoUri || data.referenceKind === 'video') clips.push(quoted(data.title, 'Clip'));
            else if (data.imageUrl || data.referenceKind === 'image') pictures.push(`${quoted(data.title, 'Picture')}${tag}`);
        } else if (node.type === NodeType.CHARACTER_NODE || node.type === NodeType.PROP_NODE || node.type === NodeType.LOCATION_NODE) {
            const name = shortText(data.entityData?.name || data.title, 60);
            if (!name) continue;
            const description = shortText(data.entityData?.description, 240);
            const line = `${name}${description ? ` — ${description}` : ''}${data.imageUrl ? ' (has a picture)' : ''}`;
            (node.type === NodeType.CHARACTER_NODE ? characters : node.type === NodeType.PROP_NODE ? props : places).push(line);
        }
    }
    const parts = [
        pictures.length ? `Reference pictures: ${pictures.join(', ')}` : null,
        clips.length ? `Reference clips: ${clips.join(', ')}` : null,
        audio.length ? `Reference audio: ${audio.join(', ')}` : null,
        characters.length ? `Characters:\n${characters.map(line => `- ${line}`).join('\n')}` : null,
        props.length ? `Props:\n${props.map(line => `- ${line}`).join('\n')}` : null,
        places.length ? `Places:\n${places.map(line => `- ${line}`).join('\n')}` : null,
    ].filter(Boolean);
    if (parts.length === 0) return '';
    return `WHAT THE WORKSPACE HOLDS, to name in REFERENCES where it helps (the person attaches them to the node in the order you list; name a sheet or a plate that is not there yet as something to make first):\n${parts.join('\n')}`.slice(0, 3000);
};

// ── The skill ────────────────────────────────────────────────────────────────

export type SkillFiles = Record<string, string>;

/** Which of the skill's files go along for each kind of prompt: the method, the failures, the filter, and for video the worked example */
export const SKILL_FILES_FOR: Record<PromptTarget, string[]> = {
    video: ['SKILL.md', 'references/video-method.md', 'references/failure-modes.md', 'references/filter-safety.md', 'references/example-film.md'],
    image: ['SKILL.md', 'references/image-method.md', 'references/failure-modes.md', 'references/filter-safety.md'],
};

const withoutFrontMatter = (text: string): string => text.replace(/^---\n[\s\S]*?\n---\n/, '');

/** The skill as one text: its files for this kind of prompt, each under its name */
export const promptSkillText = (target: PromptTarget, files: SkillFiles): string =>
    SKILL_FILES_FOR[target].map(name => `━━━ ${name} ━━━\n\n${withoutFrontMatter(files[name] ?? '').trim()}`).join('\n\n');

let skillFiles: Promise<SkillFiles> | null = null;
/** The skill's files, read once (their own piece of the app, loaded when a prompt is first asked for) */
export const loadPromptSkillFiles = (): Promise<SkillFiles> => (skillFiles ??= import('./promptSkillFiles').then(module => module.PROMPT_SKILL_FILES));
export const loadPromptSkill = async (target: PromptTarget): Promise<string> => promptSkillText(target, await loadPromptSkillFiles());

/**
 * What the model is, for this asking: the skill's own words, with the two rules that read
 * differently on a board (the answer is the file; notes go in its version note).
 */
export const promptWriterSystem = (skill: string, target: PromptTarget): string => [
    `You are the generation prompt writer of Noodle Studio, writing on the Ideas board of a small film and video team. What you write is pasted into ${target === 'video' ? 'a Video' : 'an Image'} node of the team's workspace, which sends it to the generator with the references attached to the node, in order.`,
    'Work by the skill below. Two of its rules read differently here: you write no .txt file and point nobody to one, because your whole answer IS the prompt file, written straight onto a card of the board; and notes for the person go in the version note of the file\'s header, not in chat. Answer with one prompt file in the skill\'s format and nothing else: no greeting, no words before the first line of = signs or after the last, no markdown, no code fences.',
    'Reference tags are written as the skill writes them, without a space: @Image1, @Image2 for pictures, @Video1 for a clip, @Audio1 for audio.',
    skill,
].join('\n\n');

export interface PromptAsk {
    brief: string;
    target: PromptTarget;
    generator: ModelDefinition | null;
    duration?: string;
    aspectRatio?: string;
    /** What the workspace holds (workspaceNotes), when anything */
    workspace?: string;
    /** The cards kept so far, to build on */
    kept?: string[];
    /** The skill's text (loadPromptSkill) */
    skill: string;
}

const DEFAULT_BRIEF: Record<PromptTarget, string> = {
    video: 'A short film of your own invention: one character, one prop, one place, one thing that happens.',
    image: 'A character sheet of your own invention.',
};

/** The request a Think up card set to Prompt makes: the brief, the generator's facts, what the workspace holds, the kept cards */
export const promptRequest = (ask: PromptAsk): TextRequest => {
    const kept = (ask.kept ?? []).filter(text => text.trim());
    return {
        system: promptWriterSystem(ask.skill, ask.target),
        prompt: [
            `Write ${ask.target === 'video' ? 'a video' : 'an image'} prompt file for this brief:\n\n${ask.brief.trim() || DEFAULT_BRIEF[ask.target]}`,
            generatorNotes(ask.generator, ask.target, { duration: ask.duration, aspectRatio: ask.aspectRatio }),
            ask.workspace?.trim() || null,
            kept.length > 0 ? `The team has kept these cards so far; they are the background of the brief:\n${kept.map(text => `- ${text.replace(/\n+/g, ' ')}`).join('\n')}` : null,
            'This is version 1 and has not been run: say so in the version note.',
        ].filter(Boolean).join('\n\n'),
        // Room for a multi-shot film with its header, references and sound; a cap, not a spend
        maxTokens: ask.target === 'video' ? 5000 : 3000,
        temperature: 0.7,
    };
};

export interface PromptFix {
    /** The prompt file as it was run */
    file: string;
    /** What came out wrong, in the person's words */
    wentWrong: string;
    target: PromptTarget;
    generator: ModelDefinition | null;
    duration?: string;
    aspectRatio?: string;
    skill: string;
}

/** The request to fix a prompt that was run and came out wrong: diagnose, fix at the source, the whole file again one version on */
export const fixPromptRequest = (fix: PromptFix): TextRequest => ({
    system: promptWriterSystem(fix.skill, fix.target),
    prompt: [
        `Here is a prompt file the team ran:\n\n${fix.file.trim()}`,
        `What came out wrong:\n${fix.wentWrong.trim() || 'It did not come out as the prompt says.'}`,
        generatorNotes(fix.generator, fix.target, { duration: fix.duration, aspectRatio: fix.aspectRatio }),
        'Read the report for its failure, not its words: find which of the twelve failure modes it is, then fix it at the source, one cause and one fix, with more specific staging and check lines rather than adjectives. Answer with the whole prompt file again: its version raised by one, the version note saying what changed and why, and every line the fix does not touch kept word for word.',
    ].join('\n\n'),
    maxTokens: fix.target === 'video' ? 5000 : 3000,
    temperature: 0.5,
});

// ── The prompt file ──────────────────────────────────────────────────────────

const RULE = /^\s*={5,}\s*$/;
const DASHES = /^\s*-{5,}\s*$/;
const OPEN = /▼▼▼/;
const CLOSE = /▲▲▲/;
const FENCE = /^\s*```/;

/** One paste of a prompt file: what goes onto a node; a multi-step job has one per step */
export interface PromptPaste {
    /** "STEP A — the sheet"; null for the one paste of a one-step file */
    label: string | null;
    text: string;
}

export interface PromptFile {
    /** The project's title from the header, as a card is named: "The Lantern Tree" */
    title: string;
    /** The header's lines (the PROJECT line, the version note, the shot list, SETTINGS, CHECK) */
    header: string;
    settings: string | null;
    check: string | null;
    /** The references to attach, in order: the tag and what it is */
    references: { tag: string; text: string }[];
    pastes: PromptPaste[];
}

/** A line that stands on its own in a paste or a header: a list item, a shot or step, a label with a colon or a dash after it */
const OWN_LINE = /^\s*(?:[-*•]\s|\d+[.)]\s|SHOT\s+\d|STEP\s+[A-Z0-9]|[A-Z][A-Z0-9 ,'&/]{2,}[:—–-])/;

/**
 * Writing wrapped at 80 columns (the skill's .txt format) as paragraphs: the lines of a paragraph
 * joined with spaces, blank lines kept between paragraphs, and lines that stand on their own
 * (a list item, a shot, a labelled line) left on their own. A node and a card wrap their own lines.
 */
export const unwrapParagraphs = (text: string): string =>
    text.replace(/\r\n/g, '\n').split(/\n[ \t]*\n/).map(paragraph => paragraph.split('\n').reduce((out, line) => {
        if (!out) return line.trimEnd();
        return OWN_LINE.test(line) ? `${out}\n${line.trimEnd()}` : `${out} ${line.trim()}`;
    }, '')).join('\n\n');

/** "THE LANTERN TREE" as "The Lantern Tree"; a title not in capitals stays as written */
const titleCase = (title: string): string => (title === title.toUpperCase() && /[A-Z]/.test(title)
    ? title.toLowerCase().replace(/(^|[\s"(—-])([a-z])/g, (_, before: string, letter: string) => before + letter.toUpperCase())
    : title);

const titleOfHeader = (header: string): string => {
    const quoted = /PROJECT\s*[—–-]+\s*["“]([^"”\n]+)["”]/i.exec(header)?.[1] ?? /PROJECT\s*[—–-]+\s*([^\n(]+)/i.exec(header)?.[1];
    return titleCase((quoted ?? '').replace(/\s+/g, ' ').trim());
};

/** The lines of a header field ("SETTINGS: …" and the lines that continue it), as one line */
const headerField = (lines: string[], name: string): string | null => {
    const at = lines.findIndex(line => new RegExp(`^\\s*${name}\\s*:`, 'i').test(line));
    if (at < 0) return null;
    const out = [lines[at].replace(new RegExp(`^\\s*${name}\\s*:\\s*`, 'i'), '')];
    for (let i = at + 1; i < lines.length; i++) {
        const line = lines[i];
        if (!line.trim() || /^\s*[A-Z][A-Z ]{2,}:/.test(line) || RULE.test(line)) break;
        out.push(line.trim());
    }
    return out.join(' ').replace(/\s+/g, ' ').trim() || null;
};

/**
 * A prompt file read: a card whose text has the skill's ▼▼▼ … ▲▲▲ paste markers is one. Null
 * for any other writing.
 */
export const parsePromptFile = (text: string): PromptFile | null => {
    const lines = text.replace(/\r\n/g, '\n').split('\n');
    const pastes: PromptPaste[] = [];
    let firstOpen = -1;
    for (let i = 0; i < lines.length; i++) {
        if (!OPEN.test(lines[i])) continue;
        if (firstOpen < 0) firstOpen = i;
        let end = lines.findIndex((line, j) => j > i && CLOSE.test(line));
        if (end < 0) end = lines.length;
        let from = i + 1;
        let to = end;
        while (from < to && (DASHES.test(lines[from]) || !lines[from].trim())) from++;
        while (to > from && (DASHES.test(lines[to - 1]) || !lines[to - 1].trim())) to--;
        // The line that names the block above its marker: "THE PROMPT", or "STEP A — the sheet"
        let labelAt = i - 1;
        while (labelAt >= 0 && (DASHES.test(lines[labelAt]) || RULE.test(lines[labelAt]) || !lines[labelAt].trim())) labelAt--;
        const label = labelAt >= 0 ? lines[labelAt].trim() : '';
        pastes.push({ label: /^THE PROMPT$/i.test(label) || !label ? null : label, text: unwrapParagraphs(lines.slice(from, to).join('\n')) });
        i = end;
    }
    if (pastes.length === 0 || !pastes.some(paste => paste.text.trim())) return null;
    const rules = lines.map((line, i) => (RULE.test(line) ? i : -1)).filter(i => i >= 0 && i < firstOpen);
    const headerLines = rules.length >= 2 ? lines.slice(rules[0] + 1, rules[1]) : rules.length === 1 ? lines.slice(rules[0] + 1, firstOpen) : lines.slice(0, firstOpen);
    const header = headerLines.join('\n').trim();
    const afterHeader = rules.length >= 2 ? lines.slice(rules[1] + 1, firstOpen) : [];
    const references = afterHeader
        .map(line => /^\s*(@[A-Za-z]+\d*)\s*=\s*(.+?)\s*$/.exec(line))
        .filter((m): m is RegExpExecArray => !!m)
        .map(m => ({ tag: m[1], text: m[2] }));
    return { title: titleOfHeader(header), header, settings: headerField(headerLines, 'SETTINGS'), check: headerField(headerLines, 'CHECK'), references, pastes };
};

/** The one paste of a prompt file, or its first; null for writing that is no prompt file */
export const pasteOf = (text: string): string | null => parsePromptFile(text)?.pastes[0]?.text ?? null;

/**
 * A model's answer as a prompt card: the file alone (whatever the model put around it, code
 * fences or a word before the first rule), named by the project's title in its header.
 */
export const promptCardFromAnswer = (answer: string, fallbackName: string): { name: string; text: string } => {
    let lines = answer.replace(/\r\n/g, '\n').split('\n').filter(line => !FENCE.test(line));
    const firstRule = lines.findIndex(line => RULE.test(line));
    if (firstRule > 0) lines = lines.slice(firstRule);
    const lastClose = lines.map((line, i) => (CLOSE.test(line) ? i : -1)).filter(i => i >= 0).pop();
    if (lastClose !== undefined) {
        const closingRule = lines.findIndex((line, i) => i > lastClose && RULE.test(line));
        lines = lines.slice(0, closingRule >= 0 ? closingRule + 1 : lines.length);
    }
    const text = lines.join('\n').trim();
    const file = parsePromptFile(text);
    return { name: (file?.title || fallbackName).slice(0, 60), text };
};
